import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  PushNotificationAction,
  PushNotificationOfferMessageReason,
  usePosPushNotificationControllerSubscribePush,
  usePosPushNotificationControllerUnsubscribePush,
  usePushNotificationControllerSubscribePush,
  usePushNotificationControllerUnsubscribePush,
  type PushNotificationActionDto,
  type PushNotificationCustomMessageDto,
  type PushNotificationMessageDto,
  type PushNotificationOfferDto,
  type PushNotificationOfferMessageDto,
  type SubscribePushNotificationDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAuth } from '../stores/authStore';
import { useNotifications } from '../stores/notificationStore';
import { usePos } from '../stores/posStore';
import { useLocalStorage } from '@vueuse/core';
import { usePosRequest } from './usePosRequest';
import { usePwaUpdate } from './usePwaUpdate';

const { VITE_VAPID_PUBLIC_KEY } = import.meta.env;
let permissionStatus: PermissionStatus | null = null;

/**
 * Push registration is never the point of the flow it hangs off - a login, a
 * POS check-in, a check-out - so a device that refuses to (un)subscribe must
 * not take that flow down with it.
 */
const ignoreFailure = <TResult>(promise: Promise<TResult>) =>
  promise.catch(() => undefined as TResult | undefined);

export const useWebPush = () => {
  const { t } = useI18n();
  const router = useRouter();
  const { addNotification } = useNotifications();
  const { mutateAsync: subscribePush } = usePushNotificationControllerSubscribePush();
  const { mutateAsync: unsubscribePush } = usePushNotificationControllerUnsubscribePush({
    mutation: { networkMode: 'offlineFirst', retry: false, throwOnError: false },
  });

  const { posRequest, withPosToken } = usePosRequest();

  // `networkMode: 'always'` because both of these go out at moments where the
  // online manager is paused: a device waiting for its first user, and one
  // being checked out after an idle sign-out (see App.vue). They would
  // otherwise suspend indefinitely and the device would keep its subscription.
  const { mutateAsync: subscribeDevicePush } = usePosPushNotificationControllerSubscribePush({
    mutation: { networkMode: 'always', retry: false, throwOnError: false },
    request: posRequest,
  });
  const { mutateAsync: unsubscribeDevicePush } = usePosPushNotificationControllerUnsubscribePush({
    mutation: { networkMode: 'always', retry: false, throwOnError: false },
    request: posRequest,
  });

  const { applyUpdate } = usePwaUpdate();

  const isNotificationGranted = ref(false);
  /** Endpoint currently registered for the signed-in *person*. */
  const subscribedEndpoint = useLocalStorage('pushSubscriptionEndpoint', null as string | null);
  /** Endpoint currently registered for the *device*; outlives user swaps. */
  const subscribedDeviceEndpoint = useLocalStorage(
    'posPushSubscriptionEndpoint',
    null as string | null,
  );

  const requestNotificationPermission = async () => {
    if (permissionStatus) permissionStatus.removeEventListener('change', handlePermissionChange);

    const permission = await Notification.requestPermission();

    isNotificationGranted.value = permission === 'granted';

    permissionStatus = await navigator.permissions.query({ name: 'notifications' });
    permissionStatus.addEventListener('change', handlePermissionChange);

    return isNotificationGranted.value;
  };

  /**
   * The browser push subscription both registrations are built on. A service
   * worker only ever has one, so a POS hand-over re-points that single endpoint
   * at the other owner rather than tearing it down and asking the push service
   * for a new one.
   */
  const getBrowserSubscription = async () => {
    const permissionGranted = await requestNotificationPermission();

    if (!permissionGranted) return null;

    const registration = await navigator.serviceWorker.ready;
    const existingSubscription = await registration.pushManager.getSubscription();

    if (existingSubscription) return existingSubscription;

    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: VITE_VAPID_PUBLIC_KEY,
    });
  };

  const toSubscribeDto = (subscription: PushSubscription): SubscribePushNotificationDto => ({
    endpoint: subscription.endpoint,
    p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
    auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
    encoding: [...PushManager.supportedContentEncodings],
  });

  const subscribeUser = async (forceResubscribe = false) => {
    // A shared device must not carry a personal push subscription: offer alerts
    // don't belong on the sales floor and would follow whoever last PIN'd in.
    // A checked-in device registers for itself instead - see `subscribeDevice`.
    if (isPosMode.value) return;

    try {
      const subscription = await getBrowserSubscription();

      if (!subscription) return;

      if (subscribedEndpoint.value === subscription.endpoint && !forceResubscribe) {
        return subscription;
      }

      await subscribePush({ data: toSubscribeDto(subscription) });

      subscribedEndpoint.value = subscription.endpoint;

      return subscription;
    } catch (err) {
      console.log('Failed to subscribe to push notifications: ', err);
      throw err;
    }
  };

  /**
   * @param keepBrowserSubscription Leave the browser's push subscription in
   *   place because the device is about to register that very same endpoint for
   *   itself. Dropping it here would only cost a round trip to the push service
   *   and hand back a different endpoint.
   */
  const unsubscribeUser = async (keepBrowserSubscription = false) => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    const endpoint = subscription?.endpoint ?? subscribedEndpoint.value;

    if (subscription && !keepBrowserSubscription) await subscription.unsubscribe();
    if (endpoint) await unsubscribePush({ data: { endpoint } });

    subscribedEndpoint.value = null;
  };

  /**
   * Register the endpoint for the *device*. What arrives afterwards is what the
   * device itself is subscribed to - its notification lists are seeded at
   * check-in and edited on the device's own settings screen, never per user.
   */
  const subscribeDevice = async (forceResubscribe = false) => {
    if (!isPosMode.value) return;

    try {
      const subscription = await getBrowserSubscription();

      if (!subscription) return;

      if (subscribedDeviceEndpoint.value === subscription.endpoint && !forceResubscribe) {
        return subscription;
      }

      // The device mutations resolve for every status rather than throwing, so
      // a rejected registration has to be read off the result - recording an
      // endpoint the server never accepted would make the guard above skip
      // every retry from here on.
      const { status } = await withPosToken(() =>
        subscribeDevicePush({ data: toSubscribeDto(subscription) }),
      );

      if (status !== 201) {
        throw new Error(`POS push subscription rejected with ${status}`);
      }

      subscribedDeviceEndpoint.value = subscription.endpoint;

      return subscription;
    } catch (err) {
      console.log('Failed to subscribe the device to push notifications: ', err);
      throw err;
    }
  };

  const unsubscribeDevice = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    const endpoint = subscription?.endpoint ?? subscribedDeviceEndpoint.value;

    if (subscription) await subscription.unsubscribe();
    if (endpoint) await withPosToken(() => unsubscribeDevicePush({ data: { endpoint } }));

    subscribedDeviceEndpoint.value = null;
  };

  const getPushNotificationAction = (
    action: PushNotificationActionDto,
    offer?: PushNotificationOfferDto,
  ): { icon?: IconDefinition; text: string; onClick: () => void } => {
    switch (action.action) {
      case PushNotificationAction.OPEN_URL:
        return {
          text: t('notification.actions.open-url'),
          onClick: () => router.push('/'),
        };
      case PushNotificationAction.REFRESH_LOGIN:
        return {
          text: t('notification.actions.logout'),
          onClick: () => router.push({ name: 'logout' }),
        };
      case PushNotificationAction.RELOAD:
        // Same path as an update the service worker picked up on its own, so a
        // waiting build is activated instead of just replaying the precache.
        return {
          text: t('notification.actions.reload'),
          onClick: () => applyUpdate(),
        };
      case PushNotificationAction.OPEN_OFFER:
        return {
          text: t('notification.actions.open-offer'),
          onClick: () => router.push({ name: 'offerSummary', params: { offerId: offer?.id } }),
        };
      case PushNotificationAction.REVIEW_OFFER:
        return {
          text: t('notification.actions.review-offer'),
          onClick: () => router.push({ name: 'offerReview', params: { offerId: offer?.id } }),
        };
      default:
        return {
          text: t('global.unknown'),
          onClick: () => console.warn('Unknown push notification action:', action),
        };
    }
  };

  const handlePushMessage = (event: MessageEvent<PushNotificationMessageDto>) => {
    if ('offer' in event.data) {
      handlePushOfferMessage(event as MessageEvent<PushNotificationOfferMessageDto>);
    } else {
      handlePushCustomMessage(event as MessageEvent<PushNotificationCustomMessageDto>);
    }
  };
  const handlePushOfferMessage = (event: MessageEvent<PushNotificationOfferMessageDto>) => {
    const { reason, offer, actions: notificationActions } = event.data;

    if (event.timeStamp && new Date(event.timeStamp) < new Date(Date.now() - 60 * 60 * 1000)) {
      console.warn('Received push message that is older than 1 hour, ignoring', event.data);
      return;
    }

    const actions = notificationActions?.map((action) => getPushNotificationAction(action, offer));

    switch (reason) {
      case PushNotificationOfferMessageReason.NEW_OFFER:
        addNotification({
          type: 'info',
          title: t('notification.offer.new-offer.title'),
          message: t('notification.offer.new-offer.message', {
            reference: offer.reference || t('global.no-reference'),
            offerId: offer.id,
            source: offer.source?.name ?? t('global.unknown'),
          }),
          image: offer.coverUrl,
          actions,
        });
        break;
      case PushNotificationOfferMessageReason.OFFER_READY:
        addNotification({
          type: 'success',
          title: t('notification.offer.offer-ready.title'),
          message: t('notification.offer.offer-ready.message', {
            reference: offer.reference || t('global.no-reference'),
            offerId: offer.id,
            source: offer.source?.name ?? t('global.unknown'),
          }),
          image: offer.coverUrl,
          actions,
        });
        break;
      case PushNotificationOfferMessageReason.REVIEW_REQUESTED:
        addNotification({
          type: 'warning',
          title: t('notification.offer.review-requested.title'),
          message: t('notification.offer.review-requested.message', {
            reference: offer.reference || t('global.no-reference'),
            offerId: offer.id,
            source: offer.source?.name ?? t('global.unknown'),
          }),
          image: offer.coverUrl,
          actions,
        });
        break;
      case PushNotificationOfferMessageReason.REVIEW_COMPLETED:
        addNotification({
          type: 'success',
          title: t('notification.offer.review-completed.title'),
          message: t('notification.offer.review-completed.message', {
            reference: offer.reference || t('global.no-reference'),
            offerId: offer.id,
            source: offer.source?.name ?? t('global.unknown'),
          }),
          image: offer.coverUrl,
          actions,
        });
        break;
      case PushNotificationOfferMessageReason.IMPORT_COMPLETED:
        addNotification({
          type: 'success',
          title: t('notification.offer.import-completed.title'),
          message: t('notification.offer.import-completed.message'),
          actions,
        });
        break;
      case PushNotificationOfferMessageReason.IMPORT_FAILED:
        addNotification({
          type: 'error',
          title: t('notification.offer.import-failed.title'),
          message: t('notification.offer.import-failed.message'),
          actions,
        });
        break;
    }
  };
  const handlePushCustomMessage = (event: MessageEvent<PushNotificationCustomMessageDto>) => {
    const { title, body, icon, image } = event.data;
    addNotification({
      type: 'info',
      title: t(title, title),
      message: t(body, body),
      image: image || icon,
      actions: event.data.actions?.map((action) => getPushNotificationAction(action)),
    });
  };

  const handlePermissionChange = function (this: PermissionStatus) {
    // On a device the endpoint belongs to the device, not to whoever is standing
    // at it - a user may not even be PIN'd in yet.
    if (isPosMode.value) {
      if (this.state === 'granted' || this.state === 'prompt') ignoreFailure(subscribeDevice());
      else if (this.state === 'denied') ignoreFailure(unsubscribeDevice());

      return;
    }

    if (!isLoggedIn.value) return;

    if (this.state === 'granted' || this.state === 'prompt') subscribeUser();
    else if (this.state === 'denied') unsubscribeUser();
  };

  const { isLoggedIn } = useAuth({
    onLogin: () => ignoreFailure(subscribeUser()),
    // A user stepping off a still-checked-in device is a session swap, not a
    // sign-out: the device's registration has to survive it, and it rides on
    // the very browser subscription a personal unsubscribe would tear down.
    onLogout: (_userId, isPosSession) => {
      if (!isPosSession) unsubscribeUser();
    },
  });

  const { isPosMode } = usePos({
    // Checking a device in hands the endpoint over. The manager's personal
    // registration goes first so their offer alerts stop reaching the sales
    // floor, then the device registers the same endpoint for itself.
    onPosStart: async () => {
      await ignoreFailure(unsubscribeUser(true));
      await ignoreFailure(subscribeDevice());
    },
    // Hangs off `onBeforePosEnd` rather than `onPosEnd` for one reason: the POS
    // token is still valid here, and it is the only credential the device
    // unsubscribe is accepted with.
    onBeforePosEnd: () => ignoreFailure(unsubscribeDevice()),
  });

  onMounted(() => {
    navigator.serviceWorker.addEventListener('message', handlePushMessage);

    // A reloaded device re-establishes the device registration itself: the
    // check-in that created it happened in an earlier session, and the user
    // standing there may not be signed in yet.
    if (isPosMode.value) ignoreFailure(subscribeDevice());
    else if (isLoggedIn.value) subscribeUser();
  });

  onBeforeUnmount(() => {
    navigator.serviceWorker.removeEventListener('message', handlePushMessage);
  });

  return {
    isNotificationGranted,
    subscribeUser,
    unsubscribeUser,
    subscribeDevice,
    unsubscribeDevice,
  };
};
